import { useNavigate } from '@tanstack/react-router';
import { Command as CommandPrimitive } from 'cmdk';
import {
  BarChart3,
  Building2,
  ChevronRight,
  ClipboardList,
  FileText,
  MessageSquare,
  Plus,
  Search,
  Settings,
  Shield,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCommandPalette } from '../../hooks/use-command-palette';
import { usePermissions } from '../../hooks/use-permissions';

interface NavEntry {
  label: string;
  description: string;
  to: string;
  icon: React.ElementType;
  permission?: string;
}

interface NavGroup {
  heading: string;
  entries: NavEntry[];
}

const GROUPS: NavGroup[] = [
  {
    heading: 'Permits',
    entries: [
      {
        label: 'Permits',
        description: 'View all permit applications',
        to: '/permits',
        icon: ClipboardList,
        permission: 'permit:view',
      },
      {
        label: 'New Permit',
        description: 'Submit a new permit application',
        to: '/permits/new',
        icon: Plus,
        permission: 'permit:create',
      },
    ],
  },
  {
    heading: 'Customers',
    entries: [
      {
        label: 'Customers',
        description: 'Browse all customer accounts',
        to: '/customers',
        icon: Building2,
        permission: 'customer:view',
      },
      {
        label: 'New Customer',
        description: 'Add a new customer to the system',
        to: '/customers/new',
        icon: Plus,
        permission: 'customer:create',
      },
    ],
  },
  {
    heading: 'Settings',
    entries: [
      {
        label: 'Dashboard',
        description: 'View analytics and stats',
        to: '/dashboard',
        icon: BarChart3,
      },
      {
        label: 'Users',
        description: 'Manage system users and access',
        to: '/users',
        icon: Users,
        permission: 'user:view',
      },
      {
        label: 'Roles',
        description: 'Manage roles and permission sets',
        to: '/users/roles',
        icon: Shield,
        permission: 'user:view',
      },
      {
        label: 'Request Types',
        description: 'Configure request type settings',
        to: '/config/request-types',
        icon: Settings,
        permission: 'config:view',
      },
      {
        label: 'Document Types',
        description: 'Configure document type settings',
        to: '/config/document-types',
        icon: FileText,
        permission: 'config:view',
      },
      {
        label: 'Survey Responses',
        description: 'View survey response data',
        to: '/survey/responses',
        icon: MessageSquare,
        permission: 'survey:view',
      },
      {
        label: 'Profile Settings',
        description: 'Edit your profile and preferences',
        to: '/settings/profile',
        icon: UserCircle,
      },
    ],
  },
];

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      setQuery('');
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const runCommand = (to: string) => {
    setOpen(false);
    navigate({ to });
  };

  const visible = (entries: NavEntry[]) =>
    entries.filter((e) => !e.permission || can(e.permission));

  const filteredGroups = GROUPS.map((g) => ({
    ...g,
    entries: visible(g.entries).filter((e) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return e.label.toLowerCase().includes(q) || e.description.toLowerCase().includes(q);
    }),
  })).filter((g) => g.entries.length > 0);

  const totalCount = filteredGroups.reduce((sum, g) => sum + g.entries.length, 0);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-start justify-center pt-[15vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      {/* Backdrop — covers sidebar too (sidebar is z-[99999]) */}
      <div
        className="absolute inset-0 bg-foreground/15 backdrop-blur-md"
        onClick={() => setOpen(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl mx-4 bg-card rounded-2xl border border-border shadow-2xl shadow-foreground/10 overflow-hidden">
        <CommandPrimitive
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <CommandPrimitive.Input
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages and actions…"
              className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 rounded-lg hover:bg-accent transition-colors"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center justify-center h-5 px-1.5 rounded border border-border bg-muted text-[10px] font-semibold text-muted-foreground">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <CommandPrimitive.List className="max-h-[360px] overflow-y-auto p-2">
            <CommandPrimitive.Empty className="py-10 text-center">
              <Search className="w-8 h-8 text-muted-foreground mx-auto mb-2.5 opacity-30" />
              <p className="text-sm font-medium text-muted-foreground">
                No results for &ldquo;{query}&rdquo;
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">Try a different keyword</p>
            </CommandPrimitive.Empty>

            {filteredGroups.map((group) => (
              <CommandPrimitive.Group
                key={group.heading}
                heading={group.heading}
                className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {group.entries.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <CommandPrimitive.Item
                      key={entry.to}
                      value={`${entry.label} ${entry.description}`}
                      onSelect={() => runCommand(entry.to)}
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors data-[selected=true]:bg-primary/10 hover:bg-accent"
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors bg-muted text-muted-foreground group-data-[selected=true]:bg-primary group-data-[selected=true]:text-white">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground group-data-[selected=true]:text-primary">
                          {entry.label}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {entry.description}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-primary shrink-0 hidden group-data-[selected=true]:block" />
                    </CommandPrimitive.Item>
                  );
                })}
              </CommandPrimitive.Group>
            ))}
          </CommandPrimitive.List>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center h-4 w-4 rounded border border-border bg-background text-[9px] font-bold">
                  ↑
                </kbd>
                <kbd className="inline-flex items-center justify-center h-4 w-4 rounded border border-border bg-background text-[9px] font-bold">
                  ↓
                </kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center h-4 px-1 rounded border border-border bg-background text-[9px] font-bold">
                  ↵
                </kbd>
                select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center h-4 px-1 rounded border border-border bg-background text-[9px] font-bold">
                  ESC
                </kbd>
                close
              </span>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </span>
          </div>
        </CommandPrimitive>
      </div>
    </div>
  );
}
